import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, Signal, ViewEncapsulation, computed, inject, signal, untracked } from '@angular/core';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {CdkSidebarService} from '@cdk/components/sidebar/sidebar.service';
import {Lote, Operacao, QuickPanelAction} from '../../../store/reducers/operacoes.reducer';
import {FormControl} from '@angular/forms';
import { MatIconButton, MatIcon, MatCheckbox, MatMenuTrigger, MatMenu, MatMenuItem, MatProgressBar, MatTooltip, MatProgressSpinner } from '@cdk/angular/material';
import { LowerCasePipe, DatePipe } from '@angular/common';
import { CdkColumnsSelectComponent } from '../../../../@cdk/components/columns/cdk-columns-select/cdk-columns-select.component';
import { CdkScrollbarDirective } from '../../../../@cdk/directives/cdk-scrollbar/cdk-scrollbar.directive';
import { MatCard, MatCardTitle, MatCardContent, MatCardActions } from '@angular/material/card';
import { TitleCasePipe } from '../../../../@cdk/pipes/title-case.pipe';
import { OperacoesStore } from 'app/store/signals/operacoes.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as fromStore from 'app/store';
import { Store } from '@ngrx/store';

@Component({
    selector: 'quick-panel',
    templateUrl: './quick-panel.component.html',
    styleUrls: ['./quick-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatIconButton, 
        MatIcon, 
        MatCheckbox, 
        MatMenuTrigger, 
        MatMenu, 
        MatMenuItem, 
        CdkColumnsSelectComponent, 
        MatProgressBar, 
        CdkScrollbarDirective, 
        MatCard, 
        MatCardTitle, 
        MatTooltip, 
        MatProgressSpinner, 
        MatCardContent, 
        MatCardActions, 
        DatePipe, 
        TitleCasePipe,
        LowerCasePipe
    ]
})
export class QuickPanelComponent implements OnInit {

    #operacoesStore = inject(OperacoesStore);
    #cdkSidebarService = inject(CdkSidebarService);
    #destroyRef = inject(DestroyRef);
    private _store = inject<Store<fromStore.State>>(Store);
    
    operacoesProcessando: Signal<number> = computed<number>(() => {
        const operacoesProcessando = this.#operacoesStore.operacoesEmProcessamento();
        return operacoesProcessando ? Object.keys(operacoesProcessando).length : 0;
    });

    operacoesPendentes: Signal<number> = computed<number>(() => {
        const operacoesProcessando = this.operacoesProcessando();
        if (operacoesProcessando === 0) {
            return 0;
        } else {
            return operacoesProcessando;
        }
    });
    
    loteAtual: Signal<Lote | null> = computed(() => {
        const loteAtualID = this.#operacoesStore.currentLote();
        const lotes = untracked(this.#operacoesStore.lotes);
        if(!loteAtualID) return null;
        return lotes[loteAtualID];
    });

    operacoes: Signal<Operacao[]> = computed(() => {
        const operacoes = this.#operacoesStore.operacoes();
        return operacoes ? Object.values(operacoes).reverse() : [];
    });

    operacoesLoteAtual: Signal<Operacao[]> = computed(() => {
        const operacoesLoteAtual = this.#operacoesStore.operacoesLoteAtual();
        return operacoesLoteAtual ? Object.values(operacoesLoteAtual) : [];
    });

    debouncedFiltro = signal<string>('todas');

    displayedOperacoes: Signal<Operacao[] | undefined> = computed(() => {
        const filtro = this.debouncedFiltro();
        const operacoes = this.operacoes();
        const operacoesLoteAtual = this.operacoesLoteAtual();
        const temLote = untracked(this.loteAtual);
        
        if (temLote && operacoesLoteAtual) {
            return filtro !== 'todas'
            ? operacoesLoteAtual.filter((operacao: any) => operacao.status == filtro)
            : operacoesLoteAtual;
        }
        
        return filtro !== 'todas'
            ? operacoes.filter((operacao: any) => operacao.status == filtro)
            : operacoes;
    });

    operacoesDesfazerLoteAtual: Signal<Operacao[]> = computed(() => {
        const operacoesDesfazerLoteAtual = this.#operacoesStore.operacoesDesfazerLoteAtual();
        return operacoesDesfazerLoteAtual ? Object.values(operacoesDesfazerLoteAtual) : [];
    });

    operacoesRefazerLoteAtual: Signal<Operacao[]> = computed(() => {
        const operacoesRefazerLoteAtual = this.#operacoesStore.operacoesRefazerLoteAtual()
        return operacoesRefazerLoteAtual ? Object.values(operacoesRefazerLoteAtual) : [];
    });

    date: Date;
    resultados: any[] = [];
    settings: any;
    lockedOpen = false;
    opcoesFiltros: any[] = [
        {
            'valor': 'todas',
            'label': 'Ver Todas'
        },
        {
            'valor': '1',
            'label': 'Apenas sucesso'
        },
        {
            'valor': '2',
            'label': 'Apenas com erros'
        },
        {
            'valor': '3',
            'label': 'Apenas canceladas'
        },
    ]
    selectedIds: string[] = [];
    isIndeterminate = false;
    filtros = new FormControl();

    constructor() {
        // Set the defaults
        this.date = new Date();
        this.settings = {
            notify: true
        };
    }

    ngOnInit(): void {
        this.filtros.setValue('todas');
        this.filtros.valueChanges.pipe(
            debounceTime(100),
            distinctUntilChanged(),
            takeUntilDestroyed(this.#destroyRef)
        ).subscribe((value) => {
            this.debouncedFiltro.set(value);
        });
    }

    doRefazerBloco(): void {
        this.selectedIds.forEach((selectedId) => {
            const operacao = this.operacoes().find(operacao => operacao.id === selectedId);
            if (operacao.status >= 2 && operacao.redo) {
                this.refazer(operacao);
            }
        });
    }

    refazer(operacao): void {
        if (Array.isArray(operacao.redo)) {
            operacao.redo.forEach((action) => {
                this._store.dispatch(action);
            });
        } else {
            this._store.dispatch(operacao.redo);
        }
    }

    doDesfazerBloco(): void {
        this.selectedIds.forEach((selectedId) => {
            const operacao = this.operacoes().find(operacao => operacao.id === selectedId);
            if (operacao.status === 1 && operacao.undo) {
                this.desfazer(operacao);
            }
        });
    }

    desfazer(operacao): void {
        if (Array.isArray(operacao.undo)) {
            operacao.undo.forEach((action) => {
                this._store.dispatch(action);
            });
        } else {
            this._store.dispatch(operacao.undo);
        }
    }

    refazerLoteAtual(): void {
        const operacoesRefazerLoteAtual = this.operacoesRefazerLoteAtual();
        operacoesRefazerLoteAtual.forEach((operacao) => {
            if (Array.isArray(operacao.redo)) {
                operacao.redo.forEach((action) => {
                    this._store.dispatch(action);
                });
            } else {
                this._store.dispatch(operacao.redo);
            }
        });
    }

    desfazerLoteAtual(): void {
        const operacoesDesfazerLoteAtual = this.operacoesDesfazerLoteAtual();
        operacoesDesfazerLoteAtual.forEach((operacao) => {
            if (Array.isArray(operacao.undo)) {
                operacao.undo.forEach((action) => {
                    this._store.dispatch(action);
                });
            } else {
                this._store.dispatch(operacao.undo);
            }
        });
    }

    verLote(lote): void {
        this.deselectAll();
        this.#operacoesStore.setCurrentLote(lote);
    }

    verOperacoes(): void {
        this.deselectAll();
        this.#operacoesStore.setCurrentLote(null);
    }

    toggleSidebarOpen(): void {
        this.#cdkSidebarService.getSidebar('quickPanel').toggleOpen();
    }

    toggleSidebarUnfold(): void {
        this.#cdkSidebarService.getSidebar('quickPanel').unfold();
    }

    toggleSidebarLock(): void {
        this.#cdkSidebarService.getSidebar('quickPanel').toggleFold();
    }

    toggleSelectAll(ev): void {
        if (this.selectedIds.length && this.selectedIds.length > 0) {
            this.deselectAll();
        } else {
            this.selectAll();
        }
    }

    selectAll(): void {
        const operacoes = this.loteAtual()
            ? this.operacoesLoteAtual()
            : this.operacoes();
    
        this.selectedIds = operacoes?.map(operacao => operacao.id) ?? [];
        this.recompute();
    }

    toggleInSelected(operacaoId): void {
        const selectedOperacoesIds = [...this.selectedIds];

        if (selectedOperacoesIds.find(id => id === operacaoId) !== undefined) {
            this.selectedIds = selectedOperacoesIds.filter(id => id !== operacaoId);
        } else {
            this.selectedIds = [...selectedOperacoesIds, operacaoId];
        }
        this.recompute();
    }

    deselectAll(): void {
        this.selectedIds = [];
        this.recompute();
    }

    recompute(): void {
        const operacoes = this.loteAtual()
            ? this.operacoesLoteAtual()
            : this.operacoes();
    
        this.isIndeterminate = (this.selectedIds.length !== operacoes.length && this.selectedIds.length > 0);
    }

    getQuickPanelActions(operacao: Operacao): QuickPanelAction[] {
        if(Array.isArray(operacao.quickPanelActions)) {
            return operacao.quickPanelActions;
        }
        return [];
    }

}
